import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import Button from "../UI/Button";
import { TRIP_TYPE_LABELS, formatINR } from "./marketplaceUtils";

// Shared booking/purchase form used by both the Service detail page ("Book Now") and the
// Package detail page ("Buy Now") — the fields that matter differ (pickup/drop/tripType for
// a taxi service vs just an optional travel date for a package) but the modal shell, submit
// flow, and error handling are identical, so one component drives both.
const BookingModal = ({ show, onClose, mode, item, price, prefill, onSubmit, submitting, error }) => {
  const isService = mode === "service";
  const taxi = item?.taxiDetails;
  const tripTypes = taxi?.tripTypes || [];
  // Whichever metered pricing model this vehicle uses (see backend bookService — never both
  // perKmPrice and extraKmCharge apply to the same booking).
  const needsKm = isService && taxi && ((taxi.perKmPrice || 0) > 0 || (taxi.extraKmCharge || 0) > 0);
  const needsHours = isService && taxi && (taxi.perHourPrice || 0) > 0;

  // Carried over from a FROM/TO/DATE/PASSENGERS taxi search (see TaxiSearchPage) so the customer
  // doesn't have to re-type what they already searched with.
  const [form, setForm] = useState({
    pickupLocation: prefill?.pickupLocation || "",
    dropLocation: prefill?.dropLocation || "",
    tripType: tripTypes[0] || "",
    passengers: prefill?.passengers || 1,
    notes: "",
    travelDate: prefill?.travelDate || "",
    couponCode: "",
    estimatedKm: "",
    estimatedHours: "",
  });
  const [localError, setLocalError] = useState("");

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  // Mirrors the server-side formula in bookService exactly — for display only, the backend is
  // always the source of truth for what's actually charged.
  const estimatedTotal = (() => {
    if (!isService || !taxi) return price;
    const km = Number(form.estimatedKm) > 0 ? Number(form.estimatedKm) : 0;
    const hours = Number(form.estimatedHours) > 0 ? Number(form.estimatedHours) : 0;
    let addOn = 0;
    if (taxi.perKmPrice > 0 && km > 0) addOn += taxi.perKmPrice * km;
    else if (taxi.extraKmCharge > 0 && km > (taxi.includedKm || 0)) addOn += taxi.extraKmCharge * (km - taxi.includedKm);
    if (taxi.perHourPrice > 0 && hours > 0) addOn += taxi.perHourPrice * hours;
    return price + addOn;
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError("");

    if (isService && item?.taxiDetails) {
      if (!form.pickupLocation.trim()) return setLocalError("Please enter a pickup location.");
      if (!form.dropLocation.trim()) return setLocalError("Please enter a drop location.");
      if (!form.travelDate) return setLocalError("Please select a travel date.");
      if (needsKm && !(Number(form.estimatedKm) > 0)) return setLocalError("Please enter the estimated distance in km.");
      if (needsHours && !(Number(form.estimatedHours) > 0)) return setLocalError("Please enter the estimated duration in hours.");
    }

    const payload = isService
      ? {
          pickupLocation: form.pickupLocation || undefined,
          dropLocation: form.dropLocation || undefined,
          tripType: form.tripType || undefined,
          passengers: form.passengers ? Number(form.passengers) : undefined,
          notes: form.notes || undefined,
          travelDate: form.travelDate || undefined,
          couponCode: form.couponCode || undefined,
          estimatedKm: needsKm && form.estimatedKm ? Number(form.estimatedKm) : undefined,
          estimatedHours: needsHours && form.estimatedHours ? Number(form.estimatedHours) : undefined,
        }
      : {
          travelDate: form.travelDate || undefined,
          couponCode: form.couponCode || undefined,
        };

    onSubmit(payload);
  };

  return (
    <Modal show={show} onHide={submitting ? undefined : onClose} centered size="lg">
      <Modal.Body className="p-0">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
          <h3 className="text-lg font-bold text-ink-900">
            {isService ? "Book this service" : "Buy this package"}
          </h3>
          {!submitting && (
            <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
              <FaTimes />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5">
          <p className="text-sm text-ink-500 mb-4 line-clamp-1">{item?.title}</p>

          {isService && item?.taxiDetails && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1">
                  Pickup location
                </label>
                <input
                  value={form.pickupLocation}
                  onChange={update("pickupLocation")}
                  className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                  placeholder="e.g. Airport Terminal 2"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1">
                  Drop location
                </label>
                <input
                  value={form.dropLocation}
                  onChange={update("dropLocation")}
                  className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                  placeholder="e.g. Hotel Taj, MG Road"
                />
              </div>
              {tripTypes.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1">Trip type</label>
                  <select
                    value={form.tripType}
                    onChange={update("tripType")}
                    className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
                  >
                    {tripTypes.map((t) => (
                      <option key={t} value={t}>
                        {TRIP_TYPE_LABELS[t] || t}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1">Passengers</label>
                <input
                  type="number"
                  min="1"
                  value={form.passengers}
                  onChange={update("passengers")}
                  className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                />
              </div>
              {needsKm && (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1">Estimated distance (km)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.estimatedKm}
                    onChange={update("estimatedKm")}
                    placeholder="e.g. 25"
                    className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                  />
                </div>
              )}
              {needsHours && (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1">Estimated duration (hours)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.estimatedHours}
                    onChange={update("estimatedHours")}
                    placeholder="e.g. 4"
                    className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                  />
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1">
                Travel date {isService && item?.taxiDetails ? "" : "(optional)"}
              </label>
              <input
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={form.travelDate}
                onChange={update("travelDate")}
                className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1">
                Coupon code (optional)
              </label>
              <input
                value={form.couponCode}
                onChange={update("couponCode")}
                className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-brand-200"
                placeholder="e.g. RASU50"
              />
            </div>
          </div>

          {isService && (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-ink-600 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={form.notes}
                onChange={update("notes")}
                rows={2}
                className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                placeholder="Anything the partner should know"
              />
            </div>
          )}

          {(localError || error) && (
            <div className="mb-4 p-3 rounded-lg bg-brand-50 border border-brand-200 text-brand-700 text-sm">
              {localError || error}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-ink-100">
            <div>
              <p className="text-xs text-ink-500">{needsKm || needsHours ? "Estimated total" : "Total payable"}</p>
              <p className="text-xl font-extrabold text-brand-600">{formatINR(estimatedTotal)}</p>
              {(needsKm || needsHours) && (
                <p className="text-[11px] text-ink-400 mt-0.5">Final amount is confirmed on the payment screen.</p>
              )}
            </div>
            <Button type="submit" variant="primary" size="lg" loading={submitting}>
              {submitting ? "Processing..." : "Proceed to pay"}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default BookingModal;
